/* eslint-disable no-console */
import React from "react";
import usePrevious from "@ui/lib/hooks/usePrevious";
// TODO: FIX FRONT IS DEPENDENT ON API TYPES

import type {
  ClientMessage,
  ServerMessage,
} from "../../../../service-diagnostics-core/src/websocket/Message";
import { MTS_ENV } from "../../lib/setupEnv";
import { useAuth, type AuthContextProps } from "react-oidc-context";

let reconnectTimeoutId: number | undefined;
let ackListenerTimeoutId: number | undefined;
let keepAliveTimeoutId: number | undefined;

const NORMAL_CLOSE_CODE = 3000; // no reconnection attempt
const ERROR_CLOSE_CODE = 3001;

export const sendMessage = (client: WebSocket | null, message: ClientMessage) => {
  if (client !== null && client.readyState === WebSocket.OPEN) {
    client.send(JSON.stringify(message));
  } else {
    console.error(
      `ws:send:${message.type}`,
      "cannot send message, client is not connected",
      message,
    );
  }
};

// sends a ping message to the server, expects a pong message back within 5 seconds or closes the connection for restart
const keepAlive = (client: WebSocket) => {
  const keepAlivePongTimeoutId = window.setTimeout(() => {
    if (client.readyState === WebSocket.OPEN)
      client.close(
        ERROR_CLOSE_CODE,
        "KeepAlive failure, no pong received from server in 5 seconds",
      );
  }, 5_000);

  sendMessage(client, { type: "ping" });
  const listener = (event: MessageEvent) => {
    const data = JSON.parse(event.data) as ServerMessage;
    if (data.type === "pong") {
      window.clearTimeout(keepAlivePongTimeoutId);
      client.removeEventListener("message", listener);
    }
  };

  client.addEventListener("message", listener);
};

const connectWS = (onConnect: (client: WebSocket) => void, auth: AuthContextProps) => {
  const token = auth.user?.access_token ?? null;

  const client = new WebSocket(
    `${MTS_ENV.COREV2_URL.replace("http", "ws")}/websockets?token=${token === null ? "" : token}`,
  );

  client.onopen = function () {
    window.clearTimeout(reconnectTimeoutId);
    ackListenerTimeoutId = window.setTimeout(() => {
      client.close(ERROR_CLOSE_CODE, "No ack received from server after 5 seconds on open");
    }, 5_000);

    const ackListener = (m: MessageEvent<string>) => {
      const message = JSON.parse(m.data) as ServerMessage;
      if (message.type === "ack") {
        client.removeEventListener("message", ackListener);
        window.clearTimeout(ackListenerTimeoutId);
        keepAliveTimeoutId = window.setInterval(() => keepAlive(client), 20_000);
        console.debug("ws:connection:success");
        onConnect(client);
      } else {
        throw new Error(
          `websocket error, unexpected message "${m.data}" received, ack message expected`,
        );
      }
    };
    client.addEventListener("message", ackListener);
  };

  client.onclose = function (event) {
    console.debug("ws:disconnected", event.code);
    window.clearTimeout(reconnectTimeoutId);
    window.clearTimeout(ackListenerTimeoutId);
    window.clearTimeout(keepAliveTimeoutId);

    // Reconnect after 5 seconds if the connection was closed on heartbeat failure or error
    if (event.code !== NORMAL_CLOSE_CODE) {
      reconnectTimeoutId = window.setTimeout(() => {
        console.debug("ws:reconnecting");
        connectWS(onConnect, auth);
      }, 5_000);
    }
  };

  client.onerror = function (event) {
    console.debug("webSocket error", event);
    client.close(ERROR_CLOSE_CODE, "webSocket error");
  };

  return () => {
    console.debug("ws:disconnect", NORMAL_CLOSE_CODE);
    client.close(NORMAL_CLOSE_CODE, "hook closed the connection"); // normal close, does not auto-reconnect
  };
};

// disconnect and reconnect the websocket client when the navigator becomes visible again
const useNavigatorVisible = () => {
  const [isNavigatorVisible, setIsNavigatorVisible] = React.useState(!document.hidden);

  React.useEffect(() => {
    const handleVisibilityChange = () => {
      setIsNavigatorVisible(!document.hidden);
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  return isNavigatorVisible;
};

// low level hook that returns a single websocket client, that can be used for any WS pattern with all ws gotchas handled
// - keep alive on both sides
// - auto reconnect
// - ack on connection
// - disconnect/connect on navigator hidden/visible
export const useWSClient = (onConnect: (client: WebSocket) => void) => {
  const auth = useAuth();
  const navigatorVisible = useNavigatorVisible();
  const [client, setClient] = React.useState<WebSocket | null>(null);
  const previousClient = usePrevious(client);

  React.useEffect(() => {
    if (!navigatorVisible) {
      setClient(null);
      return;
    }

    const disconnectWS = connectWS(setClient, auth);

    return () => {
      disconnectWS();
    };
  }, [auth, navigatorVisible]);
  if (previousClient !== client && client !== null) onConnect(client);
  return client;
};
